import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    Box, Typography, Paper, CircularProgress, Button, Divider, Container, Grid, Card, CardContent, Avatar, Chip, Stack, IconButton, Tooltip, Grow 
} from '@mui/material';
import { 
    ArrowBack as ArrowBackIcon,
    Person as PersonIcon,
    AccessTime as AccessTimeIcon,
    Email as EmailIcon,
    AddCircleOutline as AddCircleOutlineIcon,
    Reply as ReplyIcon,
    Delete as DeleteIcon,
    Subject as SubjectIcon
} from '@mui/icons-material';
import axios from 'axios';
import config from '../config';

function InboxDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [email, setEmail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchEmailDetails = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${config}/api/inbox/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setEmail(response.data);
            } catch (err) {
                console.error("Error fetching email details:", err);
                setError("Failed to load email details.");
            } finally {
                setLoading(false);
            }
        };

        fetchEmailDetails();
    }, [id]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;
    if (error) return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="error" variant="h6" gutterBottom>{error}</Typography>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/inbox')}>Back to Inbox</Button>
            </Paper>
        </Container>
    );
    if (!email) return <Box p={3}><Typography>Email not found</Typography><Button onClick={() => navigate('/inbox')}>Back to Inbox</Button></Box>;

    const handleCreateTicket = () => {
        // Parse "Name <email>" if possible, or just use raw string
        let requestedBy = email.sender;
        let senderEmail = '';
        
        // Simple regex to extract email if present in < >
        const emailMatch = email.sender.match(/<([^>]+)>/);
        if (emailMatch) {
            senderEmail = emailMatch[1];
            requestedBy = email.sender.replace(`<${senderEmail}>`, '').trim();
            requestedBy = requestedBy.replace(/"/g, ''); // Remove quotes if any
        } else if (email.sender.includes('@')) {
             senderEmail = email.sender;
        }

        // STRIP HTML TAGS for ticket description
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = email.body;
        const plainTextBody = tempDiv.textContent || tempDiv.innerText || "";

        navigate('/tickets', { 
            state: { 
                createFromEmail: {
                    title: email.subject,
                    description: plainTextBody,
                    requested_by: requestedBy,
                    sender_email: senderEmail
                } 
            } 
        });
    };

    return (
        <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
            <Grow in={true} timeout={500}>
                <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
                    {/* Header Section */}
                    <Paper elevation={0} sx={{
                        mb: 4, 
                        mt: 2, 
                        p: 4, 
                        borderRadius: 4, 
                        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
                        color: 'white',
                        boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        {/* Background Decorative Circles */}
                        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                        <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />

                        <Box sx={{ position: 'relative', zIndex: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', gap: 3 }}>
                                <Box sx={{ flex: 1 }}>
                                    <Button
                                        startIcon={<ArrowBackIcon />}
                                        onClick={() => navigate('/inbox')}
                                        sx={{ color: '#90caf9', mb: 2, pl: 0, fontWeight: 'bold', '&:hover': { color: 'white', bgcolor: 'transparent', transform: 'translateX(-4px)' }, transition: 'all 0.2s' }}
                                    >
                                        Back to Inbox
                                    </Button>
                                    
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                                        <Avatar sx={{ 
                                            width: 72, height: 72, 
                                            bgcolor: 'rgba(255,255,255,0.15)', 
                                            color: '#e3f2fd', 
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                                            border: '2px solid rgba(255,255,255,0.2)',
                                            backdropFilter: 'blur(4px)'
                                        }}>
                                            <EmailIcon fontSize="large" sx={{ color: 'white' }} />
                                        </Avatar>
                                        
                                        <Box>
                                            <Typography sx={{ 
                                                color: 'white',
                                                fontSize: { xs: '1.5rem', md: '2rem' },
                                                fontWeight: '900',
                                                letterSpacing: 0.5,
                                                mb: 1
                                            }}>
                                                {email.subject}
                                            </Typography>
                                            
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 1.5 }}>
                                                <Box sx={{ 
                                                  display: 'flex', alignItems: 'center', gap: 0.8, 
                                                  bgcolor: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)',
                                                  px: 1.8, py: 0.7, borderRadius: 3,
                                                  border: '1px solid rgba(255,255,255,0.2)',
                                                }}>
                                                  <PersonIcon sx={{ color: '#93c5fd', fontSize: 14 }} />
                                                  <Typography variant="caption" sx={{ color: 'white', fontWeight: 700, letterSpacing: 0.3, fontSize: '0.75rem' }}>
                                                    {email.sender}
                                                  </Typography>
                                                </Box>
                                                <Box sx={{ 
                                                  display: 'flex', alignItems: 'center', gap: 0.8, 
                                                  bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)',
                                                  px: 1.8, py: 0.7, borderRadius: 3,
                                                  border: '1px solid rgba(255,255,255,0.12)',
                                                }}>
                                                  <AccessTimeIcon sx={{ color: '#fde68a', fontSize: 14 }} />
                                                  <Typography variant="caption" sx={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.75rem' }}>
                                                    {email.date}
                                                  </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                                
                                {/* Right Side Actions */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', md: 'flex-end' }, gap: 1.5, width: { xs: '100%', md: 'auto' } }}>
                                    <Button 
                                        variant="contained"
                                        startIcon={<AddCircleOutlineIcon />}
                                        onClick={handleCreateTicket}
                                        sx={{ 
                                            bgcolor: 'white', 
                                            color: '#1e3c72', 
                                            fontWeight: 'bold', 
                                            py: 1, 
                                            px: 3, 
                                            borderRadius: 2, 
                                            boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                                            '&:hover': { bgcolor: '#f1f5f9', transform: 'translateY(-2px)' }, 
                                            transition: 'all 0.2s',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        Create Ticket
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>

                    <Grid container spacing={4}>
                        <Grid item xs={12} md={4} lg={3}>
                            <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #e2e8f0', bgcolor: 'white', height: '100%', overflow: 'visible', position: 'sticky', top: 24 }}>
                                <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#f8fafc', borderTopLeftRadius: 16, borderTopRightRadius: 16 }}>
                                    <Typography variant="h6" fontWeight="800" color="#1e293b" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <PersonIcon sx={{ color: '#3b82f6' }} />
                                        Sender Details
                                    </Typography>
                                </Box>
                                <CardContent sx={{ p: 3, pt: 4 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4, textAlign: 'center' }}>
                                        <Avatar sx={{ width: 80, height: 80, mb: 2, bgcolor: '#eff6ff', color: '#3b82f6', fontSize: 32, fontWeight: 'bold', boxShadow: '0 4px 14px rgba(59, 130, 246, 0.2)' }}>
                                            {email.sender.charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Typography variant="subtitle1" sx={{ fontWeight: '800', color: '#1e293b', mb: 0.5, wordBreak: 'break-word', width: '100%' }}>
                                            {email.sender}
                                        </Typography>
                                        <Chip label="Sender" size="small" sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 600, borderRadius: 1.5 }} />
                                    </Box>
                                    
                                    <Divider sx={{ my: 3 }} />
                                    
                                    <Stack spacing={3}>
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <Box sx={{ mt: 0.5, bgcolor: '#f8fafc', p: 1, borderRadius: 2, color: '#64748b' }}>
                                                <AccessTimeIcon fontSize="small" />
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5, display: 'block' }}>Date Received</Typography>
                                                <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600 }}>{email.date}</Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <Box sx={{ mt: 0.5, bgcolor: '#f8fafc', p: 1, borderRadius: 2, color: '#64748b' }}>
                                                <EmailIcon fontSize="small" />
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5, display: 'block' }}>Sync Status</Typography>
                                                <Chip label="Received" size="small" sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 600, border: 'none', height: 24 }} variant="outlined" />
                                            </Box>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={8} lg={9}>
                            <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #e2e8f0', bgcolor: 'white', minHeight: '60vh' }}>
                                <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#f8fafc', borderTopLeftRadius: 16, borderTopRightRadius: 16, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <SubjectIcon sx={{ color: '#3b82f6' }} />
                                    <Typography variant="h6" fontWeight="800" color="#1e293b">Message Body</Typography>
                                </Box>
                                <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                                    <Box sx={{ 
                                        '& img': { maxWidth: '100%', height: 'auto', borderRadius: 2 },
                                        '& a': { color: '#3b82f6', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline', color: '#2563eb' } },
                                        fontFamily: 'inherit',
                                        overflowWrap: 'break-word',
                                        wordWrap: 'break-word',
                                        color: '#334155',
                                        lineHeight: 1.6,
                                        fontSize: '1rem',
                                        '& p': { mb: 2 }
                                    }}>
                                        <div dangerouslySetInnerHTML={{ __html: email.body }} />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            </Grow>
        </Box>
    );
}

export default InboxDetails;
